import { useParams, useLocation } from 'react-router-dom'
import { useTenant } from '@/contexts'
import { useMemo } from 'react'
import { isReservedSlug, normalizeSlug } from '@/lib/slugs'

export type AppScope = 'org' | 'location'

export interface AppContext {
  /** Current organization ID */
  orgId: string | null
  /** Current organization slug/name */
  orgName: string | null
  /** Location slug from URL (if in location-scoped route) */
  locationSlug: string | null
  /** Location ID resolved from slug */
  locationId: string | null
  /** Location name resolved from slug */
  locationName: string | null
  /** Current scope: 'org' for org-wide pages, 'location' for location-scoped */
  scope: AppScope
  /** Whether the current location slug is valid */
  isValidLocation: boolean
  /** All available locations for the current org */
  locations: Array<{ id: string; slug: string; name: string; locationCode: string }>
}

/**
 * Hook to get the current application context based on URL.
 *
 * Detects whether the user is on an org-wide page or a location-scoped page
 * by analyzing the URL path.
 *
 * @example
 * // On /dashboard (org-wide)
 * const { scope, locationSlug } = useAppContext()
 * // scope = 'org', locationSlug = null
 *
 * // On /kdet/hangar-view (location-scoped)
 * const { scope, locationSlug, locationId } = useAppContext()
 * // scope = 'location', locationSlug = 'kdet', locationId = '...'
 */
export function useAppContext(): AppContext {
  const { locationSlug: urlLocationSlug } = useParams<{ locationSlug?: string }>()
  const location = useLocation()
  const { currentOrganization, locations: tenantLocations } = useTenant()

  return useMemo(() => {
    // Map locations to include slug (derived from location_code)
    const locations = (tenantLocations || []).map((loc) => ({
      id: loc.id,
      slug: normalizeSlug(loc.location_code),
      name: loc.name,
      locationCode: loc.location_code,
    }))

    // Determine scope from URL
    // If the first path segment is a reserved keyword, it's org-wide
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const firstSegment = pathSegments[0]?.toLowerCase()

    // Check if we're on an org-wide route (reserved keyword)
    const isOrgRoute = !firstSegment || isReservedSlug(firstSegment)

    if (isOrgRoute) {
      return {
        orgId: currentOrganization?.id ?? null,
        orgName: currentOrganization?.name ?? null,
        locationSlug: null,
        locationId: null,
        locationName: null,
        scope: 'org' as const,
        isValidLocation: false,
        locations,
      }
    }

    // Location-scoped route - resolve the location from the slug
    const normalizedSlug = urlLocationSlug ? normalizeSlug(urlLocationSlug) : firstSegment
    const matchedLocation = locations.find((loc) => loc.slug === normalizedSlug)

    return {
      orgId: currentOrganization?.id ?? null,
      orgName: currentOrganization?.name ?? null,
      locationSlug: normalizedSlug || null,
      locationId: matchedLocation?.id ?? null,
      locationName: matchedLocation?.name ?? null,
      scope: 'location' as const,
      isValidLocation: !!matchedLocation,
      locations,
    }
  }, [currentOrganization, tenantLocations, location.pathname, urlLocationSlug])
}
